import { useState, type FormEvent } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { asApiError } from "../api/client";
import { useLogin, useSession } from "../auth/SessionContext";

export function LoginPage() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { user } = useSession();
  const login = useLogin();
  const location = useLocation();

  if (user) {
    const next = (location.state as { from?: string })?.from ?? "/locations";
    return <Navigate to={next} replace />;
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    login.mutate(
      { username, password, remember_me: rememberMe },
      {
        onError: (err) => {
          const { code, detail } = asApiError(err);
          if (code === "AUTH_FAILED") {
            setError("Invalid Gigafy username or password.");
          } else if (code === "GIGAFY_UNREACHABLE") {
            setError("Gigafy is unreachable. Try again in a moment.");
          } else {
            setError(detail);
          }
        },
      },
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center px-4">
      <form
        onSubmit={onSubmit}
        className="w-full max-w-sm space-y-4 rounded-lg border border-slate-800 bg-slate-900 p-6"
      >
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Gigafy Chassis</h1>
          <p className="text-sm text-slate-400">
            Sign in with your Gigafy portal credentials.
          </p>
        </div>
        <label className="block text-sm">
          <span className="block text-slate-300">Username</span>
          <input
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            autoFocus
            autoComplete="username"
            required
            className="mt-1 w-full rounded border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100"
          />
        </label>
        <label className="block text-sm">
          <span className="block text-slate-300">Password</span>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="current-password"
            required
            className="mt-1 w-full rounded border border-slate-700 bg-slate-950 px-3 py-2 text-slate-100"
          />
        </label>
        <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-300">
          <input
            type="checkbox"
            checked={rememberMe}
            onChange={(e) => setRememberMe(e.target.checked)}
            className="h-4 w-4 rounded border-slate-700 bg-slate-950 accent-emerald-500"
          />
          <span>
            Remember me for 30 days
            <span className="block text-[11px] text-slate-500">
              Don't use on a shared computer.
            </span>
          </span>
        </label>
        {error && (
          <div className="rounded border border-red-900 bg-red-950 px-3 py-2 text-sm text-red-200">
            {error}
          </div>
        )}
        <button
          type="submit"
          disabled={login.isPending}
          className="w-full rounded bg-emerald-500 px-3 py-2 text-sm font-semibold text-slate-950 hover:bg-emerald-400 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {login.isPending ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </div>
  );
}
